import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Render Order Approval Email Step
 *
 * Domain-specific step that loads sales order data from the database
 * and renders the order approval/confirmation email HTML.
 *
 * This step isolates all domain knowledge (which models to load, how to
 * format the email) so the downstream send_email step stays generic.
 */
export const renderOrderApprovalEmailStep = defineStepConfig({
  type: "render_order_approval_email",
  name: "Render Order Approval Email",
  description:
    "Loads sales order and draft data, then renders the order approval/confirmation email with full order summary.",
  category: "Transformation",
  inputSchema: z.object({
    salesOrderId: z
      .uuid()
      .describe("The approved SalesOrder ID (for Oracle metadata)"),
    salesOrderDraftId: z
      .uuid()
      .describe(
        "The StagedCustomerPurchaseOrder ID (for customer, PO, line items)",
      ),
    issues: z
      .array(
        z.object({
          lineItemIndex: z.number(),
          issue: z.object({
            type: z.string(),
            details: z.string().optional(),
            excludedFromOrder: z.boolean(),
          }),
        }),
      )
      .default([])
      .describe("Approval issues from the ERP sync (may be empty)"),
  }),

  outputSchema: z.object({
    subject: z.string().describe("The rendered email subject line"),
    htmlContent: z.string().describe("The rendered HTML email body"),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 3,
    },
  },
});
